import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { UserManagementService, CreateUserRequest, CreateAdminUserRequest } from '@/services/user/userManagementService';
import { UserDetails, UserSummary } from '@/types/userDetails';
import { ApiError } from '@/types/carAuctionClient';

export const useUserManagement = (
  id?: string, 
  page: number = 1, 
  limit: number = 15, 
  searchQuery?: string,
  userType?: string,
  userRole?: string,
  includeDeleted?: boolean,
  options?: { enabled?: boolean; excludeAdmin?: boolean }
) => {
  const queryClient = useQueryClient();

  // Get user by ID query (for admin functionality)
  const userByIdQuery = useQuery<UserDetails, ApiError>({
    queryKey: ['userManagement', id, userRole],
    queryFn: () => UserManagementService.getUserById(id as string, userRole),
    enabled: Boolean(id) && (options?.enabled !== false),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Get all users query with pagination, search and filtering (for admin functionality)
  const allUsersQuery = useQuery<{
    users: UserSummary[];
    totalCount: number;
    totalPages: number;
    currentPage: number;
  }, ApiError>({
    queryKey: ['userManagement', page, limit, searchQuery, userType, includeDeleted, options?.excludeAdmin],
    queryFn: () => UserManagementService.getAllUsers(page, limit, searchQuery, userType, includeDeleted, options?.excludeAdmin),
    enabled: options?.enabled !== false,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error) => {
      // Don't retry on 403 errors (permission denied)
      if (error && typeof error === 'object' && 'status' in error && (error as ApiError).status === 403) {
        return false;
      }
      // Retry up to 2 times for other errors
      return failureCount < 2;
    },
  });

  // Update user information mutation (admin functionality)
  const updateUserMutation = useMutation({
    mutationFn: ({ userId, userData }: { userId: string; userData: Partial<UserDetails> }) => 
      UserManagementService.updateUserInfo(userId, userData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userManagement'] });
    },
  });

  // Delete user mutation (admin functionality)
  const deleteUserMutation = useMutation({
    mutationFn: (userId: string) => UserManagementService.deleteUser(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userManagement'] });
    },
  });

  const deleteAdminMutation = useMutation({
    mutationFn: (adminId: string) => UserManagementService.deleteAdmin(adminId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userManagement'] });
    },
  });

  // Create user mutation (admin functionality)
  const createUserMutation = useMutation({
    mutationFn: (userData: CreateUserRequest) => UserManagementService.createUser(userData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userManagement'] });
    },
  });

  // Create admin user mutation (admin functionality)
  const createAdminUserMutation = useMutation({
    mutationFn: (userData: CreateAdminUserRequest) => UserManagementService.createAdminUser(userData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userManagement'] });
    },
  });

  // Update admin permissions mutation
  const updateAdminPermissionsMutation = useMutation({
    mutationFn: ({ adminId, permissions, notes }: { adminId: string; permissions: {
      manageUsers?: boolean;
      manageVehicles?: boolean;
      manageSupport?: boolean;
      manageSettings?: boolean;
      manageAdminPermissions?: boolean;
      viewBidsOverview?: boolean;
      viewDashboardOverview?: boolean;
      viewLeadboxListings?: boolean;
    }; notes?: string }) => UserManagementService.updateAdminPermissions(adminId, permissions, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userManagement'] });
      // Refresh caller's own permission cache (settings tabs etc.)
      queryClient.invalidateQueries({ queryKey: ['currentAdminProfile'] });
    },
  });

  // Admin: get delivery addresses for a specific user
  const getUserDeliveryAddresses = (
    userId: string,
    options?: { enabled?: boolean }
  ) => {
    return useQuery<any[], ApiError>({
      queryKey: ["userDeliveryAddresses", userId],
      queryFn: () => UserManagementService.getUserDeliveryAddresses(userId),
      enabled: (options?.enabled !== false) && Boolean(userId),
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
    });
  };

  const fetchUserDeliveryAddressesAsync = async (userId: string) => {
    return queryClient.fetchQuery({
      queryKey: ["userDeliveryAddresses", userId],
      queryFn: () => UserManagementService.getUserDeliveryAddresses(userId),
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
    });
  };

  return {
    // Data
    users: allUsersQuery.data?.users || [],
    user: userByIdQuery.data,
    
    // Pagination data
    totalCount: allUsersQuery.data?.totalCount || 0,
    totalPages: allUsersQuery.data?.totalPages || 0,
    currentPage: allUsersQuery.data?.currentPage || 1,
    
    // Loading states
    isLoadingUsers: allUsersQuery.isLoading,
    isLoadingUser: userByIdQuery.isLoading,
    
    // Error states
    usersError: allUsersQuery.error,
    userError: userByIdQuery.error,
    
    // Update user (admin functionality)
    updateUser: updateUserMutation.mutate,
    updateUserAsync: updateUserMutation.mutateAsync,
    isUpdatingUser: updateUserMutation.isPending,
    updateUserError: updateUserMutation.error,
    updateUserSuccess: updateUserMutation.isSuccess,
    
    // Delete user (admin functionality)
    deleteUser: deleteUserMutation.mutate,
    deleteUserAsync: deleteUserMutation.mutateAsync,
    isDeletingUser: deleteUserMutation.isPending || deleteAdminMutation.isPending,
    deleteUserError: deleteUserMutation.error,
    deleteUserSuccess: deleteUserMutation.isSuccess,
    deleteAdminAsync: deleteAdminMutation.mutateAsync,
    
    // Create user (admin functionality)
    createUser: createUserMutation.mutate,
    createUserAsync: createUserMutation.mutateAsync,
    isCreatingUser: createUserMutation.isPending,
    createUserError: createUserMutation.error,
    createUserSuccess: createUserMutation.isSuccess,
    
    // Create admin user (admin functionality)
    createAdminUser: createAdminUserMutation.mutate,
    createAdminUserAsync: createAdminUserMutation.mutateAsync,
    isCreatingAdminUser: createAdminUserMutation.isPending,
    createAdminUserError: createAdminUserMutation.error,
    createAdminUserSuccess: createAdminUserMutation.isSuccess,
    
    // Update admin permissions (admin functionality)
    updateAdminPermissions: updateAdminPermissionsMutation.mutate,
    updateAdminPermissionsAsync: updateAdminPermissionsMutation.mutateAsync,
    isUpdatingAdminPermissions: updateAdminPermissionsMutation.isPending,
    updateAdminPermissionsError: updateAdminPermissionsMutation.error,
    updateAdminPermissionsSuccess: updateAdminPermissionsMutation.isSuccess,
    resetUpdateAdminPermissions: updateAdminPermissionsMutation.reset,
    
    // Reset functions
    resetUpdateUser: updateUserMutation.reset,
    resetDeleteUser: deleteUserMutation.reset,
    resetCreateUser: createUserMutation.reset,
    resetCreateAdminUser: createAdminUserMutation.reset,
    
    // Refetch functions
    refetchUsers: allUsersQuery.refetch,
    refetchUser: userByIdQuery.refetch,

    // Delivery addresses (admin)
    getUserDeliveryAddresses,
    fetchUserDeliveryAddressesAsync,
  };
};
